import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X, Heart } from "lucide-react";
import { inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function QuickView({ product, open, onClose }) {
  const { addToCart, toggleWishlist, inWishlist } = useCart();
  const hasVariants = !!product.variants?.length;
  const colors = hasVariants ? product.variants.map((v) => v.color).filter(Boolean) : (product.colors || []);
  const [color, setColor] = useState(colors[0]);
  const activeVariant = hasVariants ? (product.variants.find((v) => v.color === color) || product.variants[0]) : null;
  const rawSizes = activeVariant ? (activeVariant.sizes || []) : (product.sizes || []);
  const sizeList = rawSizes.map((s) => (typeof s === "string" ? { size: s, stock: 1 } : { size: s.size, stock: s.stock ?? 0 }));
  const firstInStock = (arr) => (arr.find((s) => (s.stock ?? 0) > 0)?.size ?? arr[0]?.size);
  const displayImg = (activeVariant?.images?.[0]) || product.images?.[0];
  const [size, setSize] = useState(firstInStock(sizeList));

  useEffect(() => {
    if (open) {
      const initColor = hasVariants ? product.variants[0]?.color : product.colors?.[0];
      const v0 = (hasVariants ? (product.variants[0]?.sizes || []) : (product.sizes || [])).map((s) => (typeof s === "string" ? { size: s, stock: 1 } : { size: s.size, stock: s.stock ?? 0 }));
      setColor(initColor);
      setSize(firstInStock(v0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product]);

  const onSelectColor = (c) => {
    setColor(c);
    if (hasVariants) {
      const vs = (product.variants.find((v) => v.color === c)?.sizes || []).map((s) => (typeof s === "string" ? { size: s, stock: 1 } : { size: s.size, stock: s.stock ?? 0 }));
      setSize(firstInStock(vs));
    }
  };

  if (!open) return null;
  const price = product.discount_price || product.price;
  const selectedSizeObj = sizeList.find((s) => s.size === size);
  const soldOut = sizeList.length > 0 && (!selectedSizeObj || (selectedSizeObj.stock ?? 0) <= 0);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="quickview-modal">
      <div className="absolute inset-0 bg-espresso/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-ivory rounded-3xl overflow-hidden max-w-3xl w-full grid grid-cols-1 md:grid-cols-2 shadow-2xl fade-in-up max-h-[90vh]">
        <button onClick={onClose} className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-ivory/90 flex items-center justify-center" data-testid="quickview-close"><X className="w-5 h-5" /></button>
        <img src={displayImg} alt={product.name} className="w-full h-64 md:h-full object-cover" />
        <div className="p-6 md:p-8 overflow-y-auto">
          <p className="overline mb-2">{product.category}</p>
          <h3 className="font-serif text-2xl text-espresso">{product.name}</h3>
          <div className="flex items-center gap-2 mt-2">
            <span className="font-semibold text-espresso">{inr(price)}</span>
            {product.discount_price && <span className="line-through text-espresso/40 text-sm">{inr(product.price)}</span>}
          </div>
          <p className="text-sm text-espresso/60 mt-4 leading-relaxed line-clamp-3">{product.description}</p>

          {colors.length > 0 && (
            <div className="mt-5">
              <p className="text-xs uppercase tracking-wider text-espresso/60 mb-2">Color: <span className="text-espresso">{color}</span></p>
              <div className="flex gap-2 flex-wrap">
                {colors.map((c) => (
                  <button key={c} onClick={() => onSelectColor(c)} className={`px-3 py-1 rounded-full text-xs border ${color === c ? "border-champagne bg-champagne/10 text-espresso" : "border-espresso/20 text-espresso/60"}`}>{c}</button>
                ))}
              </div>
            </div>
          )}

          {sizeList.length > 0 && (
            <div className="mt-4">
              <p className="text-xs uppercase tracking-wider text-espresso/60 mb-2">Size: <span className="text-espresso">{size}</span></p>
              <div className="flex gap-2 flex-wrap">
                {sizeList.map((s) => {
                  const out = (s.stock ?? 0) <= 0;
                  return (
                    <button key={s.size} onClick={() => !out && setSize(s.size)} disabled={out} className={`min-w-[40px] px-3 py-1.5 rounded-full text-xs border ${out ? "border-espresso/10 text-espresso/30 line-through cursor-not-allowed" : size === s.size ? "border-champagne bg-champagne/10 text-espresso" : "border-espresso/20 text-espresso/60"}`}>{s.size}</button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-7">
            <button onClick={() => { addToCart(product, { color, size, image: displayImg }); onClose(); }} disabled={soldOut} className="flex-1 bg-espresso text-ivory py-3 rounded-full text-sm font-medium hover:bg-espresso/90 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="quickview-add-cart">{soldOut ? "Sold Out" : "Add to Bag"}</button>
            <button onClick={() => toggleWishlist(product)} className="w-12 rounded-full border border-espresso/20 flex items-center justify-center"><Heart className={`w-5 h-5 ${inWishlist(product.id) ? "fill-[#a13b2f] text-[#a13b2f]" : "text-espresso"}`} /></button>
          </div>
          <Link to={`/product/${product.slug}`} onClick={onClose} className="block text-center text-xs text-champagne mt-4 gold-underline mx-auto w-fit">View full details</Link>
        </div>
      </div>
    </div>
  );
}
